import {
  Component,
  forwardRef,
  Input,
  TemplateRef,
  OnInit
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR
} from '@angular/forms';

import { TextErrorMessages } from './text-error-messages';

@Component({
  selector: 'ls-text',
  templateUrl: './text.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TextComponent),
      multi: true
    }
  ]
})
export class TextComponent implements ControlValueAccessor, OnInit {
  @Input()
  public disabled = false;
  @Input()
  public label: string;
  @Input()
  public minlength: number;
  @Input()
  public maxlength: number;
  @Input()
  public pattern = '';
  @Input()
  public placeHolder = '';
  @Input()
  public prepend = '';
  @Input()
  public required = false;
  @Input()
  public text = '';
  @Input()
  public type = 'text';
  @Input()
  public description: string | TemplateRef<any>;
  @Input()
  public patternErrorMessage: string;
  @Input()
  public formControl: FormControl;

  @Input()
  public autocomplete = 'new-password';

  public errorMessage: string = TextErrorMessages.NoError;

  ngOnInit() {
    if (this.formControl) {
      this.formControl.valueChanges.subscribe(() => {
        this.validate(this.formControl);
      });
    }
  }

  public writeValue(value: string): void {
    value ? (this.text = value) : (this.text = '');
  }

  public registerOnChange(fn: Function): void {
    this.propagateChange = fn;
  }

  public registerOnTouched(): void {
    return;
  }

  public propagateChange: any = () => {
    return;
  };

  private isRequiredFieldInvalid(control: FormControl): boolean {
    return !control.pristine && this.required && this.text.length === 0;
  }

  private isMinLengthInvalid(control: FormControl): boolean {
    return (
      !control.pristine && this.minlength && this.text.length < this.minlength
    );
  }

  private isPatternInvalid(control: FormControl, regExp: RegExp): boolean {
    return (
      !control.pristine &&
      this.pattern &&
      !regExp.test(this.text) &&
      this.text.length > 0
    );
  }

  private isInvalidSecondVatNumber(control: FormControl): boolean {
    return (
      control.errors && control.errors.startsWithTwo8 && control.value !== ''
    );
  }

  private isInvalidCanadianPostalCode(control: FormControl): boolean {
    return (
      control.errors &&
      control.errors.canadianPostalCodeFormat &&
      control.value !== ''
    );
  }

  private isInvalidBelgianNumber(control: FormControl): boolean {
    return control.errors && control.errors.invalidBelgianNumberFormat;
  }

  public validate(control: FormControl): void {
    if (!control.dirty) {
      this.errorMessage = TextErrorMessages.NoError;
      return;
    }

    const regExp: RegExp = new RegExp(this.pattern);
    this.text = String(this.text);

    if (this.isRequiredFieldInvalid(control)) {
      this.errorMessage = TextErrorMessages.RequiredField;
    } else if (this.isMinLengthInvalid(control)) {
      this.errorMessage = TextErrorMessages.MinLength;
    } else if (this.isPatternInvalid(control, regExp)) {
      this.errorMessage = this.patternErrorMessage
        ? this.patternErrorMessage
        : TextErrorMessages.PatternMatch + ' ' + this.placeHolder;
    } else if (this.isInvalidSecondVatNumber(control)) {
      this.errorMessage = TextErrorMessages.InvalidSecondVatNumber;
    } else if (this.isInvalidCanadianPostalCode(control)) {
      this.errorMessage = TextErrorMessages.CanadianPostalCodePattern;
    } else if (this.isInvalidBelgianNumber(control)) {
      this.errorMessage = TextErrorMessages.InvalidBelgianNumberFormat;
    } else {
      this.errorMessage = TextErrorMessages.NoError;
    }
  }

  public textValueChanged($event: any): void {
    if ($event.target.value !== this.text) {
      this.text = $event.target.value;
      this.propagateChange(this.text);
    }
  }
  public setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }
}
